import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { loginSocial } from '../../store/slices/authSlice';

export default function GoogleLoginButton({ className, buttonText = "Continue with Google" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!document.getElementById('google-gsi-script')) {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleGoogleClick = () => {
    if (!googleClientId || googleClientId.includes('your_google_client_id')) {
      toast.error('Google Client ID is missing. Please set REACT_APP_GOOGLE_CLIENT_ID in your frontend .env file.');
      return;
    }

    if (!window.google?.accounts?.oauth2) {
      toast.error('Google Sign-In is loading, please try again in a moment.');
      return;
    }

    setLoading(true);

    try {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: googleClientId,
        scope: 'email profile openid',
        callback: async (response) => {
          if (response.error) {
            setLoading(false);
            if (response.error !== 'popup_closed_by_user') {
              toast.error('Google Sign-In failed: ' + response.error);
            }
            return;
          }

          try {
            const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
              headers: { Authorization: `Bearer ${response.access_token}` },
            });
            const profile = await res.json();

            if (!profile.sub) {
              throw new Error('Could not retrieve profile from Google');
            }

            const result = await dispatch(
              loginSocial({
                social_type: 'Google',
                social_id: profile.sub,
                email: profile.email,
                name: profile.name,
                first_name: profile.given_name,
                last_name: profile.family_name,
                avatar_url: profile.picture,
                device_type: 'Web',
              })
            );

            if (loginSocial.fulfilled.match(result)) {
              const role = result.payload?.user?.role;
              navigate(
                ['super_admin', 'admin', 'moderator', 'business_owner'].includes(role)
                  ? '/admin'
                  : '/business'
              );
            }
          } catch (err) {
            toast.error(err.message || 'Failed to authenticate with backend');
          } finally {
            setLoading(false);
          }
        },
      });

      client.requestAccessToken();
    } catch (err) {
      setLoading(false);
      toast.error('Google Sign-In error: ' + err.message);
    }
  };

  return (
    <button
      type="button"
      onClick={handleGoogleClick}
      disabled={loading}
      className={className || "flex w-full items-center justify-center gap-3 rounded-xl border border-surface-200 bg-white px-4 py-2.5 text-sm font-medium text-surface-700 shadow-card hover:bg-surface-50 transition-colors mb-6"}
    >
      {loading ? (
        <div className="h-4 w-4 border-2 border-surface-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        <svg className="h-4 w-4" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      )}
      {loading ? 'Signing in with Google...' : buttonText}
    </button>
  );
}
